import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  fetchReportList,
  fetchReportEditInit,
  createReport,
  updateReport,
  deleteReport,
  fetchPowerBIReports,
  fetchReportPages,
  fetchPowerBIEmbed,
  initiateReportExport,
  fetchExportStatus,
} from '../api/dashboard'
import { fetchDetailInit } from '../api/detailInit'
import type { InitDetailRequest } from '../lib/initDetail'
import { queryKeys } from '../api/queryKeys'
import type { ReportGridFilters } from '../types/dashboard'

// Grid list
export function useDashboardReports(
  request: InitDetailRequest | null,
  filters: ReportGridFilters = { name: '', powerBIReportName: null }
) {
  const query = useQuery({
    queryKey: queryKeys.dashboard.list(request ?? { scope: 'client', clientId: '', userId: '' }, filters),
    queryFn: () => fetchReportList(request as InitDetailRequest, filters),
    enabled: !!request && (request.scope === 'user' ? !!request.userId : !!request.clientId),
  })

  return {
    ...query,
    reports: query.data?.reports ?? [],
    groupOptions: query.data?.groupOptions ?? [],
  }
}

export function useDashboardDetailInit(request: InitDetailRequest | null) {
  return useQuery({
    queryKey: queryKeys.dashboard.detailInit(request ?? { scope: 'client', clientId: '', userId: '' }),
    queryFn: () => fetchDetailInit(request as InitDetailRequest),
    enabled: !!request && (request.scope === 'user' ? !!request.userId : !!request.clientId),
  })
}

// Edit initialization
export function useDashboardEditInit(reportId: string, enabled = false) {
  return useQuery({
    queryKey: queryKeys.dashboard.editInit(reportId),
    queryFn: () => fetchReportEditInit(reportId),
    enabled: enabled && !!reportId,
  })
}

// Power BI reports for client
export function usePowerBIReports(clientId: string) {
  return useQuery({
    queryKey: queryKeys.dashboard.pbiReports(clientId),
    queryFn: () => fetchPowerBIReports(clientId),
    enabled: !!clientId,
  })
}

// Power BI report pages
export function usePowerBIReportPages(reportId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.dashboard.pbiPages(reportId),
    queryFn: () => fetchReportPages(reportId),
    enabled: enabled && !!reportId,
  })
}

// Embed token
export function usePowerBIEmbed(reportId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.dashboard.embed(reportId),
    queryFn: () => fetchPowerBIEmbed(reportId),
    enabled: enabled && !!reportId,
  })
}

// Export status (polling)
export function useExportStatus(
  reportId: string,
  exportId: string,
  shouldPoll = false
) {
  return useQuery({
    queryKey: queryKeys.dashboard.exportStatus(reportId, exportId),
    queryFn: () => fetchExportStatus(reportId, exportId),
    enabled: shouldPoll && !!reportId && !!exportId,
    refetchInterval: (query) => {
      if (!shouldPoll) return false
      const status = query.state.data?.status
      return status === 'Succeeded' || status === 'Failed' ? false : 3000
    },
  })
}

// Mutations
export function useCreateReportMutation(_clientId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createReport,
    onSuccess: () => { qc.invalidateQueries({ queryKey: queryKeys.dashboard.all }) },
  })
}

export function useUpdateReportMutation(_clientId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: updateReport,
    onSuccess: () => { qc.invalidateQueries({ queryKey: queryKeys.dashboard.all }) },
  })
}

export function useDeleteReportMutation(_clientId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: deleteReport,
    onSuccess: () => { qc.invalidateQueries({ queryKey: queryKeys.dashboard.all }) },
  })
}

export function useExportReportMutation() {
  return useMutation({ mutationFn: initiateReportExport })
}
